'use client';

import { useState, useRef, useCallback } from 'react';
import { useMediaQuery } from '@/app/hooks/use-media-query';

type BottomSheetProps = {
  children: React.ReactNode;
};

// Snap points as vh fractions
const SNAP_PEEK = 0.3;    // 30vh — default, shows ~1 card
const SNAP_HALF = 0.55;   // 55vh — shows 2-3 cards
const SNAP_FULL = 0.92;   // 92vh — near full screen
const SNAPS = [SNAP_PEEK, SNAP_HALF, SNAP_FULL];
const DRAG_THRESHOLD = 40;

function getClosestSnap(currentSnap: number, deltaY: number): number {
  const idx = SNAPS.indexOf(currentSnap);
  if (deltaY < -DRAG_THRESHOLD && idx < SNAPS.length - 1) return SNAPS[idx + 1]; // swipe up
  if (deltaY > DRAG_THRESHOLD && idx > 0) return SNAPS[idx - 1]; // swipe down
  return currentSnap;
}

export function BottomSheet({ children }: BottomSheetProps) {
  const [snap, setSnap] = useState(SNAP_PEEK);
  const isMobile = !useMediaQuery('(min-width: 768px)');
  const sheetRef = useRef<HTMLDivElement>(null);
  const dragStartY = useRef<number | null>(null);
  const dragStartSnap = useRef(SNAP_PEEK);

  const heightPct = `${snap * 100}vh`;
  const contentMaxH = `calc(${snap * 100}vh - 48px)`;

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    // Only drag from handle area (top 48px of sheet)
    const rect = sheetRef.current?.getBoundingClientRect();
    if (!rect) return;
    const relY = e.touches[0].clientY - rect.top;
    if (relY > 48) return;
    dragStartY.current = e.touches[0].clientY;
    dragStartSnap.current = snap;
  }, [snap]);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    if (dragStartY.current === null) return;
    const deltaY = e.changedTouches[0].clientY - dragStartY.current;
    dragStartY.current = null;
    setSnap(getClosestSnap(dragStartSnap.current, deltaY));
  }, []);

  if (!isMobile) return <>{children}</>;

  return (
    <div
      ref={sheetRef}
      className="absolute bottom-0 left-0 right-0 z-30 rounded-t-3xl bg-cream shadow-2xl"
      style={{ height: heightPct, transition: 'height 0.3s ease' }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Drag handle */}
      <div className="flex justify-center py-3 cursor-grab active:cursor-grabbing" role="slider" aria-label="Kéo để thay đổi kích thước" aria-valuemin={30} aria-valuemax={92} aria-valuenow={Math.round(snap * 100)}>
        <div className="w-14 h-1.5 rounded-full bg-coral-200" />
      </div>
      {/* Scrollable content */}
      <div className="overflow-y-auto" style={{ maxHeight: contentMaxH }}>
        {children}
      </div>
    </div>
  );
}
